import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash2, Save, ArrowLeft, Building2, Layers, Zap, Clock, CreditCard,
  Image, Upload, Loader2, SendHorizonal, AlertTriangle, CheckCircle2, FileEdit,
} from 'lucide-react';

interface UnitType { name: string; area_min: string; area_max: string; bedrooms: string; bathrooms: string; price_min: string; price_max: string; currency: string; floor_plan_urls: string[]; }
interface Feature { category: string; icon: string; items: string[]; }
interface Update { title: string; description: string; update_date: string; status: string; image_url: string; }
interface PaymentPlanImage { url: string; title: string; }
interface ProjectImage { url: string; is_floor_plan: boolean; }

const emptyUnit: UnitType = { name: '', area_min: '', area_max: '', bedrooms: '', bathrooms: '', price_min: '', price_max: '', currency: 'ETB', floor_plan_urls: [] };
const emptyFeature: Feature = { category: '', icon: 'Zap', items: [] };
const emptyUpdate: Update = { title: '', description: '', update_date: new Date().toISOString().split('T')[0], status: 'in_progress', image_url: '' };

const iconOptions = ['Zap', 'Building', 'Wifi', 'MapPin', 'Shield', 'Car', 'MoreHorizontal'];

const statusBanner: Record<string, { label: string; color: string; icon: any; description: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: FileEdit, description: 'Not yet submitted. Save as draft or submit for QC approval.' },
  pending_review: { label: 'Pending QC Review', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, description: 'Currently awaiting QC review. You can edit and resubmit.' },
  approved: { label: 'Approved & Live', color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2, description: 'This project is live. Submitting changes will queue them for QC review — the live version stays visible until your edits are approved.' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle, description: 'QC rejected this project. Address feedback and resubmit for review.' },
};

export function ManageProject() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: projectId } = useParams<{ id: string }>();
  const isEditMode = !!projectId;

  // Basic info
  const [nameEn, setNameEn] = useState('');
  const [nameAm, setNameAm] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descAm, setDescAm] = useState('');
  const [city, setCity] = useState('');
  const [subCity, setSubCity] = useState('');
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState('upcoming');
  const [currency, setCurrency] = useState('ETB');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [totalUnits, setTotalUnits] = useState('');
  const [availableUnits, setAvailableUnits] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');

  // Sub-sections
  const [images, setImages] = useState<ProjectImage[]>([]);
  const [isFloorPlan, setIsFloorPlan] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([{ ...emptyUnit }]);
  const [features, setFeatures] = useState<Feature[]>([{ ...emptyFeature }]);
  const [featureInput, setFeatureInput] = useState<Record<number, string>>({});
  const [updates, setUpdates] = useState<Update[]>([{ ...emptyUpdate }]);
  const [paymentPlanImages, setPaymentPlanImages] = useState<PaymentPlanImage[]>([]);

  const [saving, setSaving] = useState(false);
  const [loadedProject, setLoadedProject] = useState<any>(null);

  // Get developer profile
  const { data: devProfile } = useQuery({
    queryKey: ['my-dev-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Load project for edit mode
  const { data: existingProject, isLoading: projectLoading } = useQuery({
    queryKey: ['edit-project', projectId, devProfile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(url, is_floor_plan, sort_order, is_primary), project_unit_types(*), project_features(*), project_updates(*), project_payment_plan_images(*)')
        .eq('id', projectId!)
        .eq('developer_id', devProfile!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Project not found or you do not have permission to edit it.');
      return data;
    },
    enabled: !!projectId && !!devProfile,
  });

  // Hydrate state from loaded project (use pending_changes if present)
  useEffect(() => {
    if (!existingProject || loadedProject?.id === existingProject.id) return;
    const pc: any = existingProject.has_pending_changes && existingProject.pending_changes ? existingProject.pending_changes : null;
    const get = (field: string, fallback: any = null) => (pc && pc[field] !== undefined ? pc[field] : (existingProject as any)[field] ?? fallback);

    const nameObj = get('name', {});
    const descObj = get('description', {});
    setNameEn(nameObj?.en || '');
    setNameAm(nameObj?.am || '');
    setDescEn(descObj?.en || '');
    setDescAm(descObj?.am || '');
    setCity(get('city', '') || '');
    setSubCity(get('sub_city', '') || '');
    setAddress(get('address', '') || '');
    setStatus(get('status', 'upcoming') || 'upcoming');
    setCurrency(get('currency', 'ETB') || 'ETB');
    setPriceMin(get('price_min', '') !== null && get('price_min', '') !== undefined ? String(get('price_min')) : '');
    setPriceMax(get('price_max', '') !== null && get('price_max', '') !== undefined ? String(get('price_max')) : '');
    setTotalUnits(get('total_units', '') !== null && get('total_units', '') !== undefined ? String(get('total_units')) : '');
    setAvailableUnits(get('available_units', '') !== null && get('available_units', '') !== undefined ? String(get('available_units')) : '');
    setCompletionDate(get('completion_date', '') || '');
    setLatitude(get('latitude', '') !== null && get('latitude', '') !== undefined ? String(get('latitude')) : '');
    setLongitude(get('longitude', '') !== null && get('longitude', '') !== undefined ? String(get('longitude')) : '');

    // Images
    const imgs = (existingProject.project_images || []).slice().sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
    setImages(imgs.map((i: any) => ({ url: i.url, is_floor_plan: !!i.is_floor_plan })));

    // Unit types
    if (existingProject.project_unit_types?.length) {
      setUnitTypes(existingProject.project_unit_types.map((u: any) => ({
        name: u.name || '', area_min: u.area_min?.toString() || '', area_max: u.area_max?.toString() || '',
        bedrooms: u.bedrooms?.toString() || '', bathrooms: u.bathrooms?.toString() || '',
        price_min: u.price_min?.toString() || '', price_max: u.price_max?.toString() || '',
        currency: u.currency || 'ETB',
        floor_plan_urls: Array.isArray(u.floor_plan_urls) && u.floor_plan_urls.length
          ? u.floor_plan_urls
          : (u.floor_plan_url ? [u.floor_plan_url] : []),
      })));
    }
    // Features
    if (existingProject.project_features?.length) {
      setFeatures(existingProject.project_features.map((f: any) => ({
        category: f.category || '', icon: f.icon || 'Zap', items: f.items || [],
      })));
    }
    // Updates
    if (existingProject.project_updates?.length) {
      setUpdates(existingProject.project_updates.map((u: any) => ({
        title: u.title || '', description: u.description || '',
        update_date: u.update_date || new Date().toISOString().split('T')[0],
        status: u.status || 'in_progress', image_url: u.image_url || '',
      })));
    }
    // Payment plan images
    if ((existingProject as any).project_payment_plan_images?.length) {
      const sorted = [...(existingProject as any).project_payment_plan_images].sort(
        (a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)
      );
      setPaymentPlanImages(sorted.map((p: any) => ({
        url: p.url, title: p.title || '',
      })));
    }

    setLoadedProject(existingProject);
  }, [existingProject, loadedProject?.id]);

  // Upload file to storage
  const uploadFile = async (file: File, folder: string = 'photos'): Promise<string | null> => {
    const ext = file.name.split('.').pop() || 'jpg';
    const path = `${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('project-images').upload(path, file, { cacheControl: '3600', upsert: false });
    if (error) { toast.error(`Upload failed: ${error.message}`); return null; }
    const { data: { publicUrl } } = supabase.storage.from('project-images').getPublicUrl(path);
    return publicUrl;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const folder = isFloorPlan ? 'floor-plans' : 'photos';
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB limit`); continue; }
      const url = await uploadFile(file, folder);
      if (url) setImages(prev => [...prev, { url, is_floor_plan: isFloorPlan }]);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleUpdateImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Not an image'); return; }
    const url = await uploadFile(file, 'updates');
    if (url) setUpdates(prev => prev.map((u, j) => j === idx ? { ...u, image_url: url } : u));
    e.target.value = '';
  };

  const handleUnitFloorPlanUpload = async (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingUploads(n => n + 1);
    try {
      const newUrls: string[] = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
        const url = await uploadFile(file, 'unit-floor-plans');
        if (url) newUrls.push(url);
      }
      if (newUrls.length > 0) {
        setUnitTypes(prev => prev.map((u, j) => j === idx ? { ...u, floor_plan_urls: [...u.floor_plan_urls, ...newUrls] } : u));
      }
    } finally {
      setPendingUploads(n => Math.max(0, n - 1));
      e.target.value = '';
    }
  };

  const removeUnitFloorPlan = (unitIdx: number, imgIdx: number) => {
    setUnitTypes(prev => prev.map((u, j) => j === unitIdx
      ? { ...u, floor_plan_urls: u.floor_plan_urls.filter((_, k) => k !== imgIdx) }
      : u));
  };

  const handlePaymentPlanImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setPendingUploads(n => n + 1);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
        const url = await uploadFile(file, 'payment-plans');
        if (url) setPaymentPlanImages(prev => [...prev, { url, title: '' }]);
      }
    } finally {
      setPendingUploads(n => Math.max(0, n - 1));
      e.target.value = '';
    }
  };

  const addFeatureItem = (idx: number) => {
    const val = featureInput[idx]?.trim();
    if (!val) return;
    setFeatures(prev => prev.map((f, i) => i === idx ? { ...f, items: [...f.items, val] } : f));
    setFeatureInput(prev => ({ ...prev, [idx]: '' }));
  };

  // Build the project payload (used for both insert and pending_changes)
  const buildProjectPayload = () => ({
    name: { en: nameEn, am: nameAm || nameEn, om: '' },
    description: { en: descEn, am: descAm || descEn, om: '' },
    city,
    sub_city: subCity || null,
    address: address || null,
    status,
    currency,
    price_min: priceMin ? Number(priceMin) : null,
    price_max: priceMax ? Number(priceMax) : null,
    total_units: totalUnits ? Number(totalUnits) : null,
    available_units: availableUnits ? Number(availableUnits) : null,
    completion_date: completionDate || null,
    latitude: latitude ? Number(latitude) : null,
    longitude: longitude ? Number(longitude) : null,
  });

  // Replace child collections (used for create + edits to non-approved projects)
  const replaceChildCollections = async (pid: string) => {
    // Wipe existing
    await Promise.all([
      supabase.from('project_images').delete().eq('project_id', pid),
      supabase.from('project_unit_types').delete().eq('project_id', pid),
      supabase.from('project_features').delete().eq('project_id', pid),
      supabase.from('project_updates').delete().eq('project_id', pid),
      supabase.from('project_payment_plan_images' as any).delete().eq('project_id', pid),
    ]);

    if (images.length > 0) {
      await supabase.from('project_images').insert(
        images.map((img, i) => ({ project_id: pid, url: img.url, is_floor_plan: img.is_floor_plan, is_primary: i === 0, sort_order: i }))
      );
    }
    const validUnits = unitTypes.filter(u => u.name.trim());
    if (validUnits.length > 0) {
      await supabase.from('project_unit_types').insert(
        validUnits.map((u, i) => ({
          project_id: pid, name: u.name, sort_order: i,
          area_min: u.area_min ? Number(u.area_min) : null,
          area_max: u.area_max ? Number(u.area_max) : null,
          bedrooms: u.bedrooms ? Number(u.bedrooms) : null,
          bathrooms: u.bathrooms ? Number(u.bathrooms) : null,
          price_min: u.price_min ? Number(u.price_min) : null,
          price_max: u.price_max ? Number(u.price_max) : null,
          currency: u.currency,
          floor_plan_urls: u.floor_plan_urls || [],
        } as any))
      );
    }
    const validFeatures = features.filter(f => f.category.trim() && f.items.length > 0);
    if (validFeatures.length > 0) {
      await supabase.from('project_features').insert(
        validFeatures.map((f, i) => ({ project_id: pid, category: f.category, icon: f.icon, items: f.items, sort_order: i }))
      );
    }
    const validUpdates = updates.filter(u => u.title.trim());
    if (validUpdates.length > 0) {
      await supabase.from('project_updates').insert(
        validUpdates.map((u, i) => ({
          project_id: pid, title: u.title, description: u.description || null,
          update_date: u.update_date, status: u.status,
          image_url: u.image_url || null, sort_order: i,
        }))
      );
    }
    const validPaymentImages = paymentPlanImages.filter(p => p.url);
    if (validPaymentImages.length > 0) {
      await supabase.from('project_payment_plan_images' as any).insert(
        validPaymentImages.map((p, i) => ({
          project_id: pid, url: p.url, title: p.title || null, sort_order: i,
        }))
      );
    }
  };

  // Notify QC team about new submission/resubmission
  const notifyQC = async (pid: string, projName: string, isResubmit: boolean, isApprovedEdit: boolean) => {
    // Find last reviewer
    const { data: lastReview } = await supabase
      .from('project_review_history' as any)
      .select('reviewer_id')
      .eq('project_id', pid)
      .in('action', ['feedback_sent', 'rejected', 'approved'])
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const recipients = new Set<string>();
    if (lastReview && (lastReview as any).reviewer_id) recipients.add((lastReview as any).reviewer_id);

    // Also notify QC leads
    const { data: qcLeads } = await supabase
      .from('user_roles')
      .select('user_id')
      .in('role', ['quality_control_lead', 'quality_control_agent']);
    (qcLeads || []).forEach((r: any) => recipients.add(r.user_id));

    if (recipients.size === 0) return;

    const title = isApprovedEdit
      ? `Project edits submitted: ${projName}`
      : isResubmit
        ? `Project resubmitted: ${projName}`
        : `New project submitted: ${projName}`;
    const message = isApprovedEdit
      ? 'A developer submitted edits to a live project for QC review.'
      : isResubmit
        ? 'The developer addressed feedback and resubmitted this project.'
        : 'A new project is awaiting QC review.';

    await supabase.from('notifications').insert(
      Array.from(recipients).map(uid => ({
        user_id: uid,
        type: isApprovedEdit ? 'project_edits_pending' : 'project_resubmitted',
        title,
        message,
        metadata: { project_id: pid, action: isApprovedEdit ? 'edits_submitted' : 'resubmitted' },
      }))
    );
  };

  const handleSave = async (submitForApproval: boolean) => {
    if (!devProfile) { toast.error('Developer profile not found'); return; }
    if (!nameEn.trim() || !city.trim()) { toast.error('Project name and city are required'); return; }
    if (pendingUploads > 0 || uploading) { toast.error('Please wait for all image uploads to finish before saving.'); return; }

    setSaving(true);
    try {
      if (!isEditMode) {
        // ========== CREATE NEW PROJECT ==========
        const { data: project, error: projErr } = await supabase
          .from('projects')
          .insert({
            ...buildProjectPayload(),
            developer_id: devProfile.id,
            approval_status: submitForApproval ? 'pending_review' : 'draft',
          } as any)
          .select()
          .single();
        if (projErr) throw projErr;
        const pid = project.id;

        await replaceChildCollections(pid);

        if (submitForApproval) {
          await supabase.from('project_review_history' as any).insert({
            project_id: pid, reviewer_id: user!.id, action: 'resubmitted', notes: 'Initial submission for review',
          });
          await notifyQC(pid, nameEn, false, false);
        }

        queryClient.invalidateQueries({ queryKey: ['my-projects'] });
        queryClient.invalidateQueries({ queryKey: ['developer-stats'] });
        toast.success(submitForApproval ? 'Project submitted for QC approval!' : 'Project saved as draft.');
        navigate(submitForApproval ? '/dashboard/my-projects' : `/dashboard/projects/${pid}/edit`);
        return;
      }

      // ========== EDIT MODE ==========
      const pid = projectId!;
      const wasApproved = loadedProject?.approval_status === 'approved';

      if (wasApproved && submitForApproval) {
        // Option B: stash edits in pending_changes, keep live row intact
        const pendingPayload = {
          ...buildProjectPayload(),
          // Snapshot child collections too so QC can review them
          _images: images,
          _unit_types: unitTypes.filter(u => u.name.trim()),
          _features: features.filter(f => f.category.trim() && f.items.length > 0),
          _updates: updates.filter(u => u.title.trim()),
          _payment_plan_images: paymentPlanImages.filter(p => p.url),
        };
        const { error: updErr } = await supabase
          .from('projects')
          .update({
            pending_changes: pendingPayload,
            has_pending_changes: true,
            reviewer_notes: null,
          } as any)
          .eq('id', pid);
        if (updErr) throw updErr;

        await supabase.from('project_review_history' as any).insert({
          project_id: pid, reviewer_id: user!.id, action: 'resubmitted', notes: 'Edited approved project — submitted for re-approval',
        });
        await notifyQC(pid, nameEn, true, true);

        queryClient.invalidateQueries({ queryKey: ['my-projects'] });
        queryClient.invalidateQueries({ queryKey: ['edit-project'] });
        toast.success('Edits submitted for QC review. Your live project remains visible until approved.');
        navigate('/dashboard/my-projects');
        return;
      }

      // For draft/pending/rejected, write directly to live row
      const newApproval = submitForApproval ? 'pending_review' : (loadedProject?.approval_status || 'draft');
      const updatePayload: any = {
        ...buildProjectPayload(),
        approval_status: newApproval,
      };
      if (submitForApproval) {
        // Clear stale reviewer notes when resubmitting
        updatePayload.reviewer_notes = null;
      }

      const { error: updErr } = await supabase
        .from('projects')
        .update(updatePayload)
        .eq('id', pid);
      if (updErr) throw updErr;

      await replaceChildCollections(pid);

      if (submitForApproval) {
        await supabase.from('project_review_history' as any).insert({
          project_id: pid, reviewer_id: user!.id, action: 'resubmitted', notes: 'Edited and resubmitted for review',
        });
        await notifyQC(pid, nameEn, true, false);
      }

      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      queryClient.invalidateQueries({ queryKey: ['edit-project'] });
      queryClient.invalidateQueries({ queryKey: ['developer-stats'] });
      toast.success(submitForApproval ? 'Submitted for QC review!' : 'Changes saved.');
      if (submitForApproval) navigate('/dashboard/my-projects');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  if (!devProfile) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">You need a developer profile to manage projects.</p>
      </div>
    );
  }

  if (isEditMode && projectLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentStatus = loadedProject?.approval_status || 'draft';
  const banner = statusBanner[currentStatus] || statusBanner.draft;
  const BannerIcon = banner.icon;
  const wasApproved = currentStatus === 'approved';
  const hasPending = !!loadedProject?.has_pending_changes;
  const submitLabel = !isEditMode
    ? 'Submit for Approval'
    : wasApproved
      ? 'Submit Edits for Review'
      : 'Submit for Review';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
          <h1 className="font-display text-2xl font-bold">
            {isEditMode ? 'Edit Project' : 'Add New Project'}
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(!isEditMode || currentStatus === 'draft') && (
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" /> Save as Draft
            </Button>
          )}
          {isEditMode && currentStatus !== 'draft' && !wasApproved && (
            <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" /> Save Changes
            </Button>
          )}
          <Button onClick={() => handleSave(true)} disabled={saving} className="gap-2">
            <SendHorizonal className="h-4 w-4" /> {saving ? 'Saving...' : submitLabel}
          </Button>
        </div>
      </div>

      {/* Status banner */}
      {isEditMode && (
        <Card className={`border ${banner.color}`}>
          <CardContent className="py-4 flex items-start gap-3">
            <BannerIcon className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={banner.color}>{banner.label}</Badge>
                {hasPending && <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50">Edits pending QC review</Badge>}
              </div>
              <p className="text-sm">{banner.description}</p>
              {hasPending && (
                <p className="text-xs text-muted-foreground italic">
                  You're editing pending changes. Submitting again will overwrite your previously submitted edits.
                </p>
              )}
              {loadedProject?.reviewer_notes && (
                <div className="mt-2 p-2 rounded bg-background/60 border text-xs">
                  <strong>Latest QC feedback:</strong> {loadedProject.reviewer_notes}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Basic Info */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /> Basic Information</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Project Name (English) *</Label><Input value={nameEn} onChange={e => setNameEn(e.target.value)} placeholder="Enter project name" /></div>
            <div><Label>Project Name (Amharic)</Label><Input value={nameAm} onChange={e => setNameAm(e.target.value)} placeholder="የፕሮጀክት ስም" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Description (English)</Label><Textarea value={descEn} onChange={e => setDescEn(e.target.value)} rows={3} placeholder="Describe the project..." /></div>
            <div><Label>Description (Amharic)</Label><Textarea value={descAm} onChange={e => setDescAm(e.target.value)} rows={3} placeholder="ስለ ፕሮጀክቱ..." /></div>
          </div>
          <Separator />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>City *</Label><Input value={city} onChange={e => setCity(e.target.value)} placeholder="Addis Ababa" /></div>
            <div><Label>Sub City</Label><Input value={subCity} onChange={e => setSubCity(e.target.value)} placeholder="Bole" /></div>
            <div><Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="under_construction">Under Construction</SelectItem>
                  <SelectItem value="ready">Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Price Min</Label><Input type="number" value={priceMin} onChange={e => setPriceMin(e.target.value)} /></div>
            <div><Label>Price Max</Label><Input type="number" value={priceMax} onChange={e => setPriceMax(e.target.value)} /></div>
            <div><Label>Total Units</Label><Input type="number" value={totalUnits} onChange={e => setTotalUnits(e.target.value)} /></div>
            <div><Label>Available Units</Label><Input type="number" value={availableUnits} onChange={e => setAvailableUnits(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><Label>Address</Label><Input value={address} onChange={e => setAddress(e.target.value)} /></div>
            <div><Label>Completion Date</Label><Input type="date" value={completionDate} onChange={e => setCompletionDate(e.target.value)} /></div>
            <div><Label>Latitude</Label><Input type="number" step="any" value={latitude} onChange={e => setLatitude(e.target.value)} /></div>
            <div><Label>Longitude</Label><Input type="number" step="any" value={longitude} onChange={e => setLongitude(e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Project Images */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Image className="h-5 w-5 text-primary" /> Project Photos & Floor Plans</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={isFloorPlan} onChange={e => setIsFloorPlan(e.target.checked)} className="rounded" />
              <span className="text-muted-foreground">Mark as Floor Plan</span>
            </label>
            <label className="flex-1 min-w-[200px]">
              <div className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" /> Uploading...
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Upload className="h-8 w-8" />
                    <span className="text-sm font-medium">Click to upload images</span>
                    <span className="text-xs">PNG, JPG, WEBP up to 10MB each</span>
                  </div>
                )}
                <input type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" disabled={uploading} />
              </div>
            </label>
          </div>
          {images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative group border rounded-lg overflow-hidden">
                  <img src={img.url} alt="" className="w-full h-24 object-cover" />
                  {img.is_floor_plan && <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">Floor Plan</span>}
                  <button onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unit Types */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-primary" /> Unit Types (Overview)</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setUnitTypes(prev => [...prev, { ...emptyUnit }])}><Plus className="h-4 w-4 mr-1" /> Add Unit</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {unitTypes.map((unit, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Unit {i + 1}</Label>
                {unitTypes.length > 1 && <Button variant="ghost" size="sm" onClick={() => setUnitTypes(prev => prev.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Name *</Label><Input value={unit.name} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, name: e.target.value } : u))} placeholder="e.g. 2 Bed Apartment" /></div>
                <div><Label className="text-xs">Area Min (m²)</Label><Input type="number" value={unit.area_min} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, area_min: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Area Max (m²)</Label><Input type="number" value={unit.area_max} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, area_max: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Bedrooms</Label><Input type="number" value={unit.bedrooms} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, bedrooms: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Bathrooms</Label><Input type="number" value={unit.bathrooms} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, bathrooms: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Price Min</Label><Input type="number" value={unit.price_min} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, price_min: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Price Max</Label><Input type="number" value={unit.price_max} onChange={e => setUnitTypes(prev => prev.map((u, j) => j === i ? { ...u, price_max: e.target.value } : u))} /></div>
              </div>
              {/* Floor Plans upload (multi) */}
              <div className="pt-2 border-t">
                <Label className="text-xs flex items-center gap-1.5">
                  <Image className="h-3.5 w-3.5" /> Floor Plans
                  <span className="text-muted-foreground font-normal">— add multiple layouts for this unit type</span>
                </Label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {unit.floor_plan_urls.map((url, k) => (
                    <div key={k} className="relative group border rounded-lg overflow-hidden">
                      <img src={url} alt={`Floor plan ${k + 1}`} className="w-full h-24 object-cover" />
                      <button
                        type="button"
                        onClick={() => removeUnitFloorPlan(i, k)}
                        className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove floor plan"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed rounded-lg h-24 flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:border-primary/50 hover:bg-primary/5 transition-colors">
                      <Upload className="h-5 w-5" />
                      <span>{unit.floor_plan_urls.length > 0 ? 'Add More' : 'Upload Floor Plans'}</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={e => handleUnitFloorPlanUpload(e, i)}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Features */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-primary" /> Features</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setFeatures(prev => [...prev, { ...emptyFeature }])}><Plus className="h-4 w-4 mr-1" /> Add Category</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {features.map((feat, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Feature Category {i + 1}</Label>
                {features.length > 1 && <Button variant="ghost" size="sm" onClick={() => setFeatures(prev => prev.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Category Name *</Label><Input value={feat.category} onChange={e => setFeatures(prev => prev.map((f, j) => j === i ? { ...f, category: e.target.value } : f))} placeholder="e.g. Main Features" /></div>
                <div><Label className="text-xs">Icon</Label>
                  <Select value={feat.icon} onValueChange={v => setFeatures(prev => prev.map((f, j) => j === i ? { ...f, icon: v } : f))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{iconOptions.map(ic => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Input value={featureInput[i] || ''} onChange={e => setFeatureInput(prev => ({ ...prev, [i]: e.target.value }))}
                  placeholder="Add a feature item..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFeatureItem(i))} />
                <Button type="button" variant="outline" size="sm" onClick={() => addFeatureItem(i)}><Plus className="h-4 w-4" /></Button>
              </div>
              {feat.items.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {feat.items.map((item, k) => (
                    <span key={k} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                      {item}
                      <button onClick={() => setFeatures(prev => prev.map((f, j) => j === i ? { ...f, items: f.items.filter((_, m) => m !== k) } : f))}
                        className="hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Development Updates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> Development Updates</CardTitle>
            <Button variant="outline" size="sm" onClick={() => setUpdates(prev => [...prev, { ...emptyUpdate }])}><Plus className="h-4 w-4 mr-1" /> Add Update</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {updates.map((upd, i) => (
            <div key={i} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Update {i + 1}</Label>
                {updates.length > 1 && <Button variant="ghost" size="sm" onClick={() => setUpdates(prev => prev.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Title *</Label><Input value={upd.title} onChange={e => setUpdates(prev => prev.map((u, j) => j === i ? { ...u, title: e.target.value } : u))} placeholder="Construction Update" /></div>
                <div><Label className="text-xs">Date</Label><Input type="date" value={upd.update_date} onChange={e => setUpdates(prev => prev.map((u, j) => j === i ? { ...u, update_date: e.target.value } : u))} /></div>
                <div><Label className="text-xs">Status</Label>
                  <Select value={upd.status} onValueChange={v => setUpdates(prev => prev.map((u, j) => j === i ? { ...u, status: v } : u))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="upcoming">Upcoming</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Update Image</Label>
                  <div className="flex items-center gap-2">
                    {upd.image_url && <img src={upd.image_url} alt="" className="h-10 w-14 object-cover rounded border" />}
                    <label className="flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
                      <Upload className="h-3.5 w-3.5" /> Upload
                      <input type="file" accept="image/*" onChange={e => handleUpdateImageUpload(e, i)} className="hidden" />
                    </label>
                  </div>
                </div>
              </div>
              <div><Label className="text-xs">Description</Label><Textarea value={upd.description} onChange={e => setUpdates(prev => prev.map((u, j) => j === i ? { ...u, description: e.target.value } : u))} rows={2} /></div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Payment Plan Images */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Payment Plans</CardTitle>
            <label>
              <span className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 border rounded-md cursor-pointer hover:bg-muted transition-colors">
                <Upload className="h-4 w-4" /> Upload Image
              </span>
              <input type="file" accept="image/*" multiple onChange={handlePaymentPlanImageUpload} className="hidden" />
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Upload payment plan images (e.g. screenshots or scans of your payment schedule). Buyers will see them as a photo gallery on the project detail page.
          </p>
          {paymentPlanImages.length === 0 ? (
            <label className="block">
              <div className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Upload className="h-8 w-8" />
                  <span className="text-sm font-medium">Click to upload payment plan images</span>
                  <span className="text-xs">PNG, JPG, WEBP up to 10MB each</span>
                </div>
                <input type="file" accept="image/*" multiple onChange={handlePaymentPlanImageUpload} className="hidden" />
              </div>
            </label>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {paymentPlanImages.map((p, i) => (
                <div key={i} className="border rounded-lg p-3 flex gap-3 items-start">
                  <img src={p.url} alt={p.title || `Payment plan ${i + 1}`} className="h-24 w-32 object-cover rounded shrink-0 border" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div>
                      <Label className="text-xs">Caption (optional)</Label>
                      <Input
                        value={p.title}
                        onChange={e => setPaymentPlanImages(prev => prev.map((pp, j) => j === i ? { ...pp, title: e.target.value } : pp))}
                        placeholder="e.g. Hotel Suites Payment Plan"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPaymentPlanImages(prev => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-4 w-4 text-destructive mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom save */}
      <div className="flex justify-end gap-3 pb-8 flex-wrap">
        {(!isEditMode || currentStatus === 'draft') && (
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} size="lg" className="gap-2">
            <Save className="h-4 w-4" /> Save as Draft
          </Button>
        )}
        {isEditMode && currentStatus !== 'draft' && !wasApproved && (
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving} size="lg" className="gap-2">
            <Save className="h-4 w-4" /> Save Changes
          </Button>
        )}
        <Button onClick={() => handleSave(true)} disabled={saving} size="lg" className="gap-2">
          <SendHorizonal className="h-4 w-4" /> {saving ? 'Submitting...' : submitLabel}
        </Button>
      </div>
    </div>
  );
}
